import { Component, OnInit, HostListener } from '@angular/core'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { ShippingService } from './shipping.service'
import { LoginService } from 'src/app/login/login.service'
import { ToastrService } from 'ngx-toastr'
import { SettingsService } from '../settings/settings.service'
import { MatDialog } from '@angular/material/dialog'
import { checkPriceWarning, getAllPricingTiers, FincartPricingTier } from './fincart-pricing.constant'
import { FincartPricingDialogComponent } from './fincart-pricing-dialog/fincart-pricing-dialog.component'

interface Area {
  areaId: string
  areaName: string
  defaultSelection: boolean
  areaPrice: string
  showAreaDropdown?: boolean
  areaSearch?: string
  filteredAreas?: string[]
  isDeleting?: boolean
  showMenu?: boolean
}
interface City {
  cityId: string
  cityName: string
  defaultSelection: boolean
  cityPrice: string
  areas: Area[]
  availableAreas?: string[]
  showCityDropdown?: boolean
  citySearch?: string
  filteredCities?: string[]
  isDeleting?: boolean
  showMenu?: boolean
}

interface Governorate {
  governorateId: string
  governorateName: string
  governoratePrice: string
  defaultSelection: boolean
  isoCode: string
  cities?: City[]
  areas?: Area[]
  selectedState?: any
  availableCities?: string[]
  availableAreas?: string[]
  showGovernorateDropdown?: boolean
  governorateSearch?: string
  filteredGovernorates?: string[]
  isDeleting?: boolean
  showMenu?: boolean
}

interface Country {
  countryId: string
  countryName: string
  defaultSelection: boolean
  isoCode: string
  countryPrice: string
  governorates: Governorate[]
  selectedCountry?: any
  states?: any[]
  availableGovernorates?: string[]
  showCountryDropdown?: boolean
  countrySearch?: string
  filteredCountries?: any[]
  showMenu?: boolean
}

@Component({
  selector: 'app-shipping',
  templateUrl: './shipping.component.html',
  styleUrls: ['./shipping.component.scss']
})
export class ShippingComponent implements OnInit {
  constructor(
    public builderService: BuilderService,
    public loginService: LoginService,
    public shippingService: ShippingService,
    private toastService: ToastrService,
    private settingsService: SettingsService,
    private dialog: MatDialog,
  ) {}
  mobile = false
  allCountries: any[] = []
  countries: Country[] = []
  saveState = false
  countriesProgress = false
  isLoading = false
  private fincartPopulated = false

  get fincartActivated(): boolean {
    return this.builderService.fincartActive
  }

  // Pagination properties
  pageSize = 5 // Number of items per page
  pageIndex = 0 // Current page index (0-based)

  /**
   * Returns the countries to display on the current page
   */
  get paginatedCountries() {
    const startIndex = this.pageIndex * this.pageSize
    const endIndex = startIndex + this.pageSize
    return this.countries.slice(startIndex, endIndex)
  }

  /**
   * Returns the total number of pages
   */
  get totalPages() {
    return Math.ceil(this.countries.length / this.pageSize)
  }

  /**
   * Navigate to the next page
   */
  nextPage() {
    if (this.pageIndex < this.totalPages - 1) {
      this.pageIndex++
    }
  }

  /**
   * Navigate to the previous page
   */
  previousPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--
    }
  }

  // Dropdown toggle methods
  toggleCountryDropdown(event: Event, index: number) {
    event.stopPropagation()
    const country = this.paginatedCountries[index]
    country.showCountryDropdown = !country.showCountryDropdown
    if (country.showCountryDropdown) {
      country.filteredCountries = [...this.allCountries]
    }
  }

  toggleGovernorateDropdown(event: Event, gov: Governorate) {
    event.stopPropagation()
    gov.showGovernorateDropdown = !gov.showGovernorateDropdown
    if (gov.showGovernorateDropdown) {
      const country = this.countries.find(c => c.governorates?.includes(gov))
      gov.filteredGovernorates = country?.availableGovernorates || []
    }
  }

  toggleCityDropdown(event: Event, city: City) {
    event.stopPropagation()
    city.showCityDropdown = !city.showCityDropdown
    if (city.showCityDropdown) {
      const gov = this.countries.flatMap(c => c.governorates || []).find(g => g.cities?.includes(city))
      city.filteredCities = gov?.availableCities || []
    }
  }

  toggleAreaDropdown(event: Event, area: Area) {
    event.stopPropagation()
    area.showAreaDropdown = !area.showAreaDropdown
    if (area.showAreaDropdown) {
      const city = this.countries.flatMap(c => c.governorates || []).flatMap(g => g.cities || []).find(ct => ct.areas?.includes(area))
      area.filteredAreas = city?.availableAreas || []
    }
  }

  toggleAreaDropdownGov(event: Event, area: Area) {
    event.stopPropagation()
    area.showAreaDropdown = !area.showAreaDropdown
    if (area.showAreaDropdown) {
      const gov = this.countries.flatMap(c => c.governorates || []).find(g => g.areas?.includes(area))
      area.filteredAreas = gov?.availableAreas || []
    }
  }

  toggleMenu(event: Event, item: any) {
    event.stopPropagation()
    const wasOpen = item.showMenu
    this.closeAllMenus()
    item.showMenu = !wasOpen
  }

  closeAllMenus() {
    this.countries.forEach(c => {
      c.showMenu = false
      c.governorates?.forEach(g => {
        g.showMenu = false
        g.cities?.forEach(city => {
          city.showMenu = false
          city.areas?.forEach((a: Area) => { a.showMenu = false })
        })
        g.areas?.forEach((a: Area) => { a.showMenu = false })
      })
    })
  }

  // Filter methods
  filterCountries(country: Country) {
    const search = (country.countrySearch || '').toLowerCase()
    country.filteredCountries = search
      ? this.allCountries.filter(c => c.countryName.toLowerCase().includes(search))
      : [...this.allCountries]
  }

  filterGovernorates(gov: Governorate, country: Country) {
    const search = (gov.governorateSearch || '').toLowerCase()
    gov.filteredGovernorates = search
      ? (country.availableGovernorates || []).filter(g => g.toLowerCase().includes(search))
      : (country.availableGovernorates || [])
  }

  filterCities(city: City, gov: Governorate) {
    const search = (city.citySearch || '').toLowerCase()
    city.filteredCities = search
      ? (gov.availableCities || []).filter(c => c.toLowerCase().includes(search))
      : (gov.availableCities || [])
  }

  filterAreas(area: Area, city: City) {
    const search = (area.areaSearch || '').toLowerCase()
    area.filteredAreas = search
      ? (city.availableAreas || []).filter(a => a.toLowerCase().includes(search))
      : (city.availableAreas || [])
  }

  filterAreasGov(area: Area, gov: Governorate) {
    const search = (area.areaSearch || '').toLowerCase()
    area.filteredAreas = search
      ? (gov.availableAreas || []).filter(a => a.toLowerCase().includes(search))
      : (gov.availableAreas || [])
  }

  // Selection methods
  selectCountry(country: Country, selectedCountry: any) {
    country.selectedCountry = selectedCountry
    country.showCountryDropdown = false
    country.countrySearch = ''
    this.onCountrySelect(country)
  }

  selectGovernorate(gov: Governorate, country: Country, govName: string) {
    gov.governorateName = govName
    gov.showGovernorateDropdown = false
    gov.governorateSearch = ''
    this.onGovernorateSelect(gov, country)
  }

  selectCity(city: City, gov: Governorate, cityName: string) {
    city.cityName = cityName
    city.showCityDropdown = false
    city.citySearch = ''
    this.onCitySelect(city, gov)
  }

  selectArea(area: Area, city: City, areaName: string) {
    area.areaName = areaName
    area.showAreaDropdown = false
    area.areaSearch = ''
    this.activateSave()
  }

  selectAreaGov(area: Area, gov: Governorate, areaName: string) {
    area.areaName = areaName
    area.showAreaDropdown = false
    area.areaSearch = ''
    this.activateSave()
  }

  ngOnInit(): void {
    this.getAllCountries()
    this.checkFincartActivation()
    // Close dropdowns when clicking outside
    document.addEventListener('click', () => {
      this.closeAllMenus()
      this.countries.forEach(country => {
        country.showCountryDropdown = false
        country.governorates?.forEach(gov => {
          gov.showGovernorateDropdown = false
          gov.cities?.forEach(city => {
            city.showCityDropdown = false
            city.areas?.forEach(area => {
              area.showAreaDropdown = false
            })
          })
          gov.areas?.forEach(area => {
            area.showAreaDropdown = false
          })
        })
      })
    })
    this.shippingService.getCountries(true).subscribe((countries: any) => {
      if (countries.length) {
        this.countries = countries.map((c: any) => ({
          ...c,
          selectedCountry: this.allCountries.find((ac) => ac.countryName === c.countryName) || null,
          isoCode: c.isoCode || '',
          states: [],
          governorates: c.governorates?.map((g: any) => {
            if (c.countryName === 'Egypt') {
              return {
                ...g,
                selectedState: null,
                areas:
                  g.areas?.map((area: any) => ({
                    ...area
                  })) || [],
                availableAreas: []
              }
            } else {
              return {
                ...g,
                selectedState: null,
                cities:
                  g.cities?.map((city: any) => ({
                    ...city,
                    areas: city.areas || [],
                    availableAreas: []
                  })) || [],
                availableCities: []
              }
            }
          })
        }))
        this.countries.forEach((country) => {
          if (country.selectedCountry && country.isoCode) {
            this.shippingService.getStates(country.isoCode).subscribe(
              (states) => {
                country.states = states
                // Map states to available governorate names
                country.availableGovernorates = states.map((state: any) => state.stateName)
                country.governorates.forEach((gov) => {
                  const state = states.find((s) => s.isoCode === gov.isoCode)
                  if (state) {
                    gov.selectedState = state
                    gov.isoCode = state.isoCode // Ensure isoCode is set
                    if (country.countryName === 'Egypt') {
                      this.getAreasForGovernorate(gov.governorateName).subscribe(
                        (areas) => {
                          gov.availableAreas = areas
                        },
                        (error) => {
                          console.log(`Error fetching areas for governorate ${gov.governorateName}:`, error)
                          gov.availableAreas = []
                        }
                      )
                    } else {
                      this.shippingService.getCities(country.isoCode, gov.isoCode).subscribe(
                        (cities) => {
                          gov.availableCities = cities
                          gov.cities?.forEach((city) => {
                            if (city.cityName) {
                              this.shippingService.getAreas(city.cityName).subscribe(
                                (areas) => {
                                  city.availableAreas = areas
                                },
                                (error) => {
                                  console.log(`Error fetching areas for city ${city.cityName}:`, error)
                                  city.availableAreas = []
                                }
                              )
                            }
                          })
                        },
                        (error) => {
                          console.log(`Error fetching cities for governorate ${gov.governorateName}:`, error)
                          gov.availableCities = []
                        }
                      )
                    }
                  }
                })
              },
              (error) => {
                console.log(`Error fetching states for country ${country.countryName}:`, error)
                country.states = []
                country.availableGovernorates = []
              }
            )
          }
        })
      }
      if (!Array.isArray(this.countries)) {
        this.countries = []
      }
    })
    this.mobile = window.innerWidth <= 768
  }

  @HostListener('window:resize')
  onResize() {
    this.mobile = window.innerWidth <= 768
  }

  /**
   * Adds a new country entry to the countries list.
   */
  changePageSize(size: number): void {
    this.pageSize = Number(size)
    this.pageIndex = 0
  }

  addCountry() {
    this.countries.push({
      countryId: this.generateUniqueId(),
      selectedCountry: null,
      countryName: '',
      isoCode: '',
      countryPrice: '',
      governorates: [],
      defaultSelection: false,
      states: []
    })
    this.pageIndex = this.totalPages - 1
    this.activateSave()
  }

  removeCountry(index: number) {
    this.countries.splice(index, 1)
    this.activateSave()
  }

  private getShippingValidationMessage(country?: Country, governorate?: Governorate, city?: City, availableResults?: any[]): string | null {
    if (country && !country.isoCode) return 'Please select a country first'
    if (governorate && !governorate.isoCode) return 'Please select a state first'
    if (city && !city.cityName) return 'Please select a city first'
    if (availableResults && !availableResults.length) {
      if (city) return 'No areas available for this city'
      if (governorate) return 'No cities available for this state'
      if (country) return 'No states available for this country'
    }
    return null
  }

  private validateShippingContext(country?: Country, governorate?: Governorate, city?: City, availableResults?: any[]): boolean {
    const message = this.getShippingValidationMessage(country, governorate, city, availableResults)
    if (message) {
      this.toastService.info(message, 'Info')
      return false
    }
    return true
  }

  addGovernorate(country: Country) {
    if (!this.validateShippingContext(country)) return
    this.shippingService.getStates(country.isoCode).subscribe(
      (states) => {
        if (!this.validateShippingContext(country, undefined, undefined, states)) return
        country.states = states
        // Map states to available governorate names
        country.availableGovernorates = states.map((state: any) => state.stateName)
        country.governorates.push({
          governorateId: this.generateUniqueId(),
          governorateName: '',
          governoratePrice: '',
          defaultSelection: false,
          isoCode: '',
          cities: country.countryName !== 'Egypt' ? [] : undefined,
          areas: country.countryName === 'Egypt' ? [] : undefined,
          availableCities: country.countryName !== 'Egypt' ? [] : undefined,
          availableAreas: country.countryName === 'Egypt' ? [] : undefined,
          selectedState: null
        })
        this.activateSave()
      },
      (error) => {
        console.log(`Error fetching states for country ${country.countryName}:`, error)
        country.states = []
        country.availableGovernorates = []
      }
    )
  }

  removeGov(country: Country, index: number) {
    const gov = country.governorates[index]
    gov.isDeleting = true
    setTimeout(() => {
      country.governorates.splice(index, 1)
      this.activateSave()
    }, 300)
  }

  addCity(country: Country, governorate: Governorate) {
    if (country.countryName === 'Egypt') return
    if (!this.validateShippingContext(country, governorate)) return
    this.shippingService.getCities(country.isoCode, governorate.isoCode).subscribe(
      (cities) => {
        if (!this.validateShippingContext(country, governorate, undefined, cities)) return
        governorate.availableCities = cities
        governorate.cities?.push({
          cityId: this.generateUniqueId(),
          cityName: '',
          cityPrice: '',
          areas: [],
          defaultSelection: false,
          availableAreas: []
        })
        this.activateSave()
      },
      (error) => {
        console.log(`Error fetching cities for governorate ${governorate.governorateName}:`, error)
        governorate.availableCities = []
      }
    )
  }

  removeCity(country: Country, governorate: Governorate, index: number) {
    const city = governorate.cities?.[index]
    if (city) {
      city.isDeleting = true
      setTimeout(() => {
        governorate.cities?.splice(index, 1)
        this.activateSave()
      }, 300)
    }
  }

  addArea(country: Country, governorate: Governorate, city: City) {
    if (country.countryName === 'Egypt') return
    if (!this.validateShippingContext(country, governorate, city)) return
    this.shippingService.getAreas(city.cityName).subscribe(
      (areas) => {
        if (!this.validateShippingContext(country, governorate, city, areas)) return
        city.availableAreas = areas
        city.areas.push({
          areaId: this.generateUniqueId(),
          areaName: '',
          defaultSelection: false,
          areaPrice: ''
        })
        this.activateSave()
      },
      (error) => {
        console.log(`Error fetching areas for city ${city.cityName}:`, error)
        city.availableAreas = []
      }
    )
  }

  removeArea(country: Country, governorate: Governorate, city: City, index: number) {
    city.areas.splice(index, 1)
    this.activateSave()
  }

  addAreaToGovernorate(country: Country, governorate: Governorate) {
    if (country.countryName !== 'Egypt') return
    if (!this.validateShippingContext(country, governorate)) return
    this.getAreasForGovernorate(governorate.governorateName).subscribe(
      (areas) => {
        if (!this.validateShippingContext(country, governorate, undefined, areas)) return
        governorate.availableAreas = areas
        governorate.areas?.push({
          areaId: this.generateUniqueId(),
          areaName: '',
          defaultSelection: false,
          areaPrice: ''
        })
        this.activateSave()
      },
      (error) => {
        console.log(`Error fetching areas for governorate ${governorate.governorateName}:`, error)
        governorate.availableAreas = []
      }
    )
  }

  removeAreaFromGovernorate(country: Country, governorate: Governorate, index: number) {
  const area = governorate.areas?.[index]
  if (!area) return

  area.isDeleting = true

  setTimeout(() => {
    governorate.areas?.splice(index, 1)
    this.activateSave()
  }, 300)
}


  onCountrySelect(country: Country) {
    if (country.selectedCountry) {
      country.countryName = country.selectedCountry.countryName
      country.isoCode = country.selectedCountry.isoCode
      this.shippingService.getStates(country.isoCode).subscribe(
        (states) => {
          country.states = states // Fetch states for the new country
          // Map states to available governorate names
          country.availableGovernorates = states.map((state: any) => state.stateName)
          country.governorates = [] // Reset governorates when country changes
        },
        (error) => {
          console.error(`Error fetching states for country ${country.countryName}:`, error)
          country.states = []
          country.availableGovernorates = []
        }
      )
    } else {
      country.countryName = ''
      country.isoCode = ''
      country.governorates = []
      country.states = []
      country.availableGovernorates = []
    }
    this.activateSave()
  }

  onGovernorateSelect(governorate: Governorate, country: Country) {
    if (governorate.governorateName) {
      // Find the selected state from available states
      const selectedState = country?.states?.find((s) => s.stateName === governorate.governorateName)
      if (selectedState) {
        governorate.selectedState = selectedState
        governorate.isoCode = selectedState.isoCode

        if (country.countryName === 'Egypt') {
          governorate.areas = []
          governorate.availableAreas = []

          // If fincart is activated, auto-populate areas from fincart
          if (this.fincartActivated) {
            this.autoPopulateFincartAreas(governorate)
          } else {
            this.getAreasForGovernorate(governorate.governorateName).subscribe(
              (areas) => {
                governorate.availableAreas = areas
              },
              (error) => {
                console.log(`Error fetching areas for governorate ${governorate.governorateName}:`, error)
                governorate.availableAreas = []
              }
            )
          }
        } else {
          governorate.cities = []
          governorate.availableCities = []
          this.shippingService.getCities(country.isoCode, governorate.isoCode).subscribe(
            (cities) => {
              governorate.availableCities = cities
            },
            (error) => {
              console.log(`Error fetching cities for governorate ${governorate.governorateName}:`, error)
              governorate.availableCities = []
            }
          )
        }
      }
    } else {
      governorate.selectedState = null
      governorate.isoCode = ''
    }
    this.activateSave()
  }

  onStateSelect(governorate: Governorate, country: Country) {
    if (governorate.isoCode) {
      const selectedState = country?.states?.find((s) => s.isoCode === governorate.isoCode)
      if (selectedState) {
        governorate.selectedState = selectedState
        governorate.governorateName = selectedState.stateName
        if (country.countryName === 'Egypt') {
          governorate.areas = []
          governorate.availableAreas = []

          // If fincart is activated, auto-populate areas from fincart
          if (this.fincartActivated) {
            this.autoPopulateFincartAreas(governorate)
          } else {
            this.getAreasForGovernorate(governorate.governorateName).subscribe(
              (areas) => {
                governorate.availableAreas = areas
              },
              (error) => {
                console.log(`Error fetching areas for governorate ${governorate.governorateName}:`, error)
                governorate.availableAreas = []
              }
            )
          }
        } else {
          governorate.cities = []
          governorate.availableCities = []
          this.shippingService.getCities(country.isoCode, governorate.isoCode).subscribe(
            (cities) => {
              governorate.availableCities = cities
            },
            (error) => {
              console.log(`Error fetching cities for governorate ${governorate.governorateName}:`, error)
              governorate.availableCities = []
            }
          )
        }
      } else {
        governorate.selectedState = null
        governorate.governorateName = ''
        governorate.isoCode = ''
      }
    } else {
      governorate.selectedState = null
      governorate.governorateName = ''
    }
    this.activateSave()
  }
  onCitySelect(city: City, governorate: Governorate) {
    if (city.cityName) {
      this.shippingService.getAreas(city.cityName).subscribe(
        (areas) => {
          city.availableAreas = areas
          city.areas = []
          this.activateSave()
        },
        (error) => {
          console.log(`Error fetching areas for city ${city.cityName}:`, error)
          city.availableAreas = []
        }
      )
    }
    this.activateSave()
  }
  saveCountries() {
    if (this.isLoading) return

  this.isLoading = true
    const countriesToSave = this.countries.filter((c: Country) => !!c.countryName).map((c: Country) => ({
      countryId: c.countryId,
      countryName: c.countryName,
      isoCode: c.isoCode,
      defaultSelection: c.defaultSelection,
      countryPrice: c.countryPrice,
      governorates: c.governorates?.filter((g: Governorate) => !!g.governorateName).map((g: Governorate) => {
        if (c.countryName === 'Egypt') {
          return {
            governorateId: g.governorateId,
            governorateName: g.governorateName,
            governoratePrice: g.governoratePrice,
            defaultSelection: g.defaultSelection,
            isoCode: g.isoCode,
            areas: g.areas?.map((area: Area) => ({
              areaId: area.areaId,
              areaName: area.areaName,
              areaPrice: area.areaPrice
            }))
          }
        } else {
          return {
            governorateId: g.governorateId,
            governorateName: g.governorateName,
            defaultSelection: g.defaultSelection,
            governoratePrice: g.governoratePrice,
            isoCode: g.isoCode,
            cities: g.cities?.map((city: City) => ({
              cityId: city.cityId,
              cityName: city.cityName,
              defaultSelection: city.defaultSelection,
              cityPrice: city.cityPrice,
              areas: city.areas?.map((area: Area) => ({
                areaId: area.areaId,
                defaultSelection: area.defaultSelection,
                areaName: area.areaName,
                areaPrice: area.areaPrice
              }))
            }))
          }
        }
      })
    }))
    this.countriesProgress = true
    this.shippingService.submitCountry(countriesToSave).subscribe((result: any) => {
      this.countriesProgress = false
      this.saveState = false
      this.toastService.success('Shipping settings have been saved successfully', 'Success')
       this.isLoading = false
    })
  }

  activateSave() {
    this.saveState = true
  }

  generateUniqueId = () => {
    const timestamp = new Date().getTime().toString()
    const random = Math.floor(Math.random() * 10000).toString()
    return timestamp + '-' + random
  }

  getAllCountries() {
    this.shippingService.getAllCountries().subscribe(
      (countries) => {
        this.allCountries = countries
      },
      (error) => {
        console.log('Error fetching all countries:', error)
        this.allCountries = []
      }
    )
  }

  checkFincartActivation() {
    this.settingsService.getSettings().subscribe((settings: any) => {
      const isActivated = !!settings?.fincartApiKey
      if (isActivated) {
        this.builderService.fincartActive = true
        this.builderService.fincartApiKey = settings.fincartApiKey
      }

      // If fincart is activated and we haven't populated yet, populate the shipping list
      if (isActivated && !this.fincartPopulated) {
        this.fincartPopulated = true
        this.populateFincartShipping()
      }
    })
  }

  /**
   * Populates the shipping list with fincart cities and areas for Egypt.
   * Adds missing cities and areas to existing configuration.
   */
  populateFincartShipping() {
    const fincartCityAreas = this.shippingService.getFincartCityAreas()
    if (!fincartCityAreas || fincartCityAreas.length === 0) return

    // Check if Egypt already exists
    let egyptCountry = this.countries.find(c => c.countryName === 'Egypt')
    console.log(fincartCityAreas)

    if (!egyptCountry) {
      // Find Egypt in allCountries to get the proper isoCode
      const egyptData = this.allCountries.find(c => c.countryName === 'Egypt')

      egyptCountry = {
        countryId: this.generateUniqueId(),
        countryName: 'Egypt',
        isoCode: egyptData?.isoCode || 'EG',
        countryPrice: '',
        defaultSelection: false,
        governorates: [],
        selectedCountry: egyptData || null,
        states: [],
        availableGovernorates: []
      }
      this.countries.push(egyptCountry)
    }

    let hasChanges = false

    // Process each fincart city
    fincartCityAreas.forEach((cityData: any) => {
      // Check if this city/governorate already exists
      const existingGov = egyptCountry!.governorates.find(
        g => g.governorateName.toLowerCase() === cityData.cityName.toLowerCase()
      )

      if (existingGov) {
        // Governorate exists - add any missing areas
        const existingAreaNames = (existingGov.areas || []).map((a: Area) => a.areaName.toLowerCase())

        cityData.areas.forEach((fincartArea: any) => {
          const areaNameEn = fincartArea.area_name_en
          if (!existingAreaNames.includes(areaNameEn.toLowerCase())) {
            // Area doesn't exist, add it
            if (!existingGov.areas) {
              existingGov.areas = []
            }
            existingGov.areas.push({
              areaId: this.generateUniqueId(),
              areaName: areaNameEn,
              areaPrice: '',
              defaultSelection: false
            })
            hasChanges = true
          }
        })

        // Update available areas list
        const allFincartAreas = cityData.areas.map((a: any) => a.area_name_en).sort()
        existingGov.availableAreas = allFincartAreas
      } else {
        // Governorate doesn't exist - add it with all areas
        const areas = cityData.areas.map((area: any) => ({
          areaId: this.generateUniqueId(),
          areaName: area.area_name_en,
          areaPrice: '',
          defaultSelection: false
        }))

        const availableAreas = cityData.areas.map((area: any) => area.area_name_en).sort()

        egyptCountry!.governorates.push({
          governorateId: this.generateUniqueId(),
          governorateName: cityData.cityName,
          governoratePrice: '',
          defaultSelection: false,
          isoCode: '',
          areas: areas,
          availableAreas: availableAreas,
          selectedState: null
        })
        hasChanges = true
      }
    })

    if (hasChanges) {
      this.activateSave()
    }
  }

  /**
   * Auto-populates a governorate with all fincart areas for that city.
   * @param governorate The governorate to populate with areas
   */
  autoPopulateFincartAreas(governorate: Governorate) {
    const fincartCityAreas = this.shippingService.getFincartCityAreas()
    const fincartCity = this.shippingService.findFincartCity(governorate.governorateName)

    if (fincartCity) {
      const cityData = fincartCityAreas.find((c: any) => c.cityName === fincartCity)

      if (cityData) {
        // Populate areas array with all fincart areas
        governorate.areas = cityData.areas.map((area: any) => ({
          areaId: this.generateUniqueId(),
          areaName: area.area_name_en,
          areaPrice: '',
          defaultSelection: false
        }))

        // Set available areas for the dropdown
        governorate.availableAreas = cityData.areas.map((a: any) => a.area_name_en).sort()

        this.activateSave()
        return
      }
    }

    // Fallback if no fincart match found
    this.getAreasForGovernorate(governorate.governorateName).subscribe(
      (areas) => {
        governorate.availableAreas = areas
      },
      (error) => {
        console.log(`Error fetching areas for governorate ${governorate.governorateName}:`, error)
        governorate.availableAreas = []
      }
    )
  }

  /**
   * Gets areas for a governorate/city.
   * When fincart is activated, uses fincart areas with fuzzy city matching.
   * @param governorateName The name of the governorate/city
   * @returns Observable emitting array of area names
   */
  getAreasForGovernorate(governorateName: string) {
    if (this.fincartActivated) {
      const fincartCity = this.shippingService.findFincartCity(governorateName)
      if (fincartCity) {
        return this.shippingService.getAreas(fincartCity)
      }
    }
    return this.shippingService.getAreas(governorateName)
  }

  /**
   * Mark exactly one item in the provided list as default.
   *
   * @template data - An object that must contain a `defaultSelection` string property.
   * @param list - The array of items to update.
   * @param selectedIndex - The index of the item to mark as the default.
   */
  private setSingleDefault<data extends { defaultSelection: boolean }>(list: data[], selectedIndex: number): void {
    for (let i = 0; i < list.length; i++) {
      list[i].defaultSelection = i === selectedIndex
    }
  }

  /**
   * Ensure that the given country is the only default country.
   * Clears defaults from all other countries and their nested governorates, cities, and areas.
   *
   * @param country - The country object to set as default.
   */
  private ensureCountryDefault(country: Country): void {
    const countryIndex = this.countries.findIndex((currentCountry) => currentCountry === country)
    if (countryIndex === -1) return

    this.setSingleDefault(this.countries, countryIndex)

    // optional: clear nested defaults in other countries
    this.countries.forEach((loopCountry, loopCountryIndex) => {
      if (loopCountryIndex === countryIndex) return
      loopCountry.governorates?.forEach((loopGovernorate) => {
        loopGovernorate.defaultSelection = false
        loopGovernorate.cities?.forEach((loopCity) => {
          loopCity.defaultSelection = false
          loopCity.areas?.forEach((loopArea) => (loopArea.defaultSelection = false))
        })
        loopGovernorate.areas?.forEach((loopArea) => (loopArea.defaultSelection = false))
      })
    })
  }

  /**
   * Ensure that the given governorate is the only default under its country.
   * Clears defaults from sibling governorates, their cities, and areas.
   *
   * @param country - The parent country object.
   * @param governorate - The governorate object to set as default.
   */
  private ensureGovernorateDefault(country: Country, governorate: Governorate): void {
    if (!country?.governorates?.length) return

    const governorateIndex = country.governorates.findIndex((currentGovernorate) => currentGovernorate === governorate)
    if (governorateIndex === -1) return

    this.setSingleDefault(country.governorates, governorateIndex)

    // clear nested defaults in sibling governorates
    country.governorates.forEach((loopGovernorate, loopGovernorateIndex) => {
      if (loopGovernorateIndex === governorateIndex) return
      loopGovernorate.defaultSelection = false
      loopGovernorate.cities?.forEach((loopCity) => {
        loopCity.defaultSelection = false
        loopCity.areas?.forEach((loopArea) => (loopArea.defaultSelection = false))
      })
      loopGovernorate.areas?.forEach((loopArea) => (loopArea.defaultSelection = false))
    })
  }

  /**
   * Ensure that the given city is the only default under its governorate.
   * Clears defaults from sibling cities and their areas.
   *
   * @param governorate - The parent governorate object.
   * @param city - The city object to set as default.
   */
  private ensureCityDefault(governorate: Governorate, city: City): void {
    if (!governorate?.cities?.length) return

    const cityIndex = governorate.cities.findIndex((currentCity) => currentCity === city)
    if (cityIndex === -1) return

    this.setSingleDefault(governorate.cities, cityIndex)

    // clear nested defaults in sibling cities
    governorate.cities.forEach((loopCity, loopCityIndex) => {
      if (loopCityIndex === cityIndex) return
      loopCity.defaultSelection = false
      loopCity.areas?.forEach((loopArea) => (loopArea.defaultSelection = false))
    })
  }

  /**
   * Ensure that the given area is the only default in its list.
   *
   * @param areas - The list of areas to update.
   * @param area - The area object to set as default.
   */
  private ensureAreaDefault(areas: Area[], area: Area): void {
    if (!Array.isArray(areas) || !areas.length) return

    const areaIndex = areas.findIndex((currentArea) => currentArea === area)
    if (areaIndex === -1) return

    this.setSingleDefault(areas, areaIndex)
  }

  /**
   * Toggle a country as default. If already default, it will be deselected.
   *
   * @param index - The index of the country in the `countries` array.
   */
  onDefaultCountry(index: number): void {
    const country = this.countries[index]
    if (!country) return

    // Toggle: if already default, deselect it
    if (country.defaultSelection) {
      country.defaultSelection = false
    } else {
      // Set this as the ONLY default country
      this.ensureCountryDefault(country)
    }

    this.activateSave()
  }
  /**
   * Toggle a governorate as default. If already default, it will be deselected.
   *
   * @param country - The parent country object.
   * @param governorateIndex - The index of the governorate in the country's governorates array.
   */

  onDefaultGovernorate(country: Country, governorateIndex: number): void {
    if (!country?.governorates?.length) return

    const governorate = country.governorates[governorateIndex]

    // Toggle: if already default, deselect it
    if (governorate.defaultSelection) {
      governorate.defaultSelection = false
    } else {
      // Set this as the default governorate and its parent country
      this.ensureGovernorateDefault(country, governorate)
      this.ensureCountryDefault(country)
    }

    this.activateSave()
  }

  /**
   * Toggle a city as default. If already default, it will be deselected.
   *
   * @param governorate - The parent governorate object.
   * @param cityIndex - The index of the city in the governorate's cities array.
   */

  onDefaultCity(governorate: Governorate, cityIndex: number): void {
    if (!governorate?.cities?.length) return

    const city = governorate.cities[cityIndex]

    // Toggle: if already default, deselect it
    if (city.defaultSelection) {
      city.defaultSelection = false
    } else {
      // Set this as the default city and its parent governorate and country
      this.ensureCityDefault(governorate, city)

      const parentCountry = this.countries.find((country) => country.governorates?.includes(governorate))
      if (parentCountry) {
        this.ensureGovernorateDefault(parentCountry, governorate)
        this.ensureCountryDefault(parentCountry)
      }
    }

    this.activateSave()
  }

  /**
   * Toggle an area (under a governorate, for Egypt) as default. If already default, it will be deselected.
   *
   * @param governorate - The parent governorate object.
   * @param areaIndex - The index of the area in the governorate's areas array.
   */
  onDefaultAreaForGovernorate(governorate: Governorate, areaIndex: number): void {
    if (!governorate?.areas?.length) return

    const area = governorate.areas[areaIndex]

    // Toggle: if already default, deselect it
    if (area.defaultSelection) {
      area.defaultSelection = false
    } else {
      // Set this as the default area and its parent governorate and country
      this.ensureAreaDefault(governorate.areas, area)

      const parentCountry = this.countries.find((country) => country.governorates?.includes(governorate))
      if (parentCountry) {
        this.ensureGovernorateDefault(parentCountry, governorate)
        this.ensureCountryDefault(parentCountry)
      }
    }

    this.activateSave()
  }

  /**
   * Toggle an area (under a city, for non-Egypt countries) as default. If already default, it will be deselected.
   *
   * @param city - The parent city object.
   * @param areaIndex - The index of the area in the city's areas array.
   */
  onDefaultAreaForCity(city: City, areaIndex: number): void {
    if (!city?.areas?.length) return

    const area = city.areas[areaIndex]

    // Toggle: if already default, deselect it
    if (area.defaultSelection) {
      area.defaultSelection = false
    } else {
      // Set this as the default area and its parent city, governorate, and country
      this.ensureAreaDefault(city.areas, area)

      const parentGovernorate = this.countries.flatMap((country) => country.governorates || []).find((gov) => gov.cities?.includes(city))

      if (parentGovernorate) {
        this.ensureCityDefault(parentGovernorate, city)

        const parentCountry = this.countries.find((country) => country.governorates?.includes(parentGovernorate))
        if (parentCountry) {
          this.ensureGovernorateDefault(parentCountry, parentGovernorate)
          this.ensureCountryDefault(parentCountry)
        }
      }
    }

    this.activateSave()
  }

  /**
   * Check if a governorate's shipping price is below the Fincart minimum
   * @param governorateName The name of the governorate
   * @param price The client's shipping price
   * @returns Warning info or null if no warning
   */
  getPriceWarning(governorateName: string, price: string): { fincartPrice: number; difference: number } | null {
    if (!this.fincartActivated) return null
    return checkPriceWarning(governorateName, price)
  }

  /**
   * Check if an area's shipping price is below the Fincart minimum (uses parent governorate's tier)
   * @param governorateName The parent governorate name
   * @param price The area's shipping price
   * @returns Warning info or null if no warning
   */
  getAreaPriceWarning(governorateName: string, price: string): { fincartPrice: number; difference: number } | null {
    if (!this.fincartActivated) return null
    return checkPriceWarning(governorateName, price)
  }

  /**
   * Open the Fincart pricing dialog
   */
  openPricingDialog(): void {
    this.dialog.open(FincartPricingDialogComponent, {
      width: '600px',
      panelClass: 'fincart-pricing-dialog-panel'
    })
  }

  /**
   * Get all Fincart pricing tiers for display
   */
  getFincartPricingTiers(): FincartPricingTier[] {
    return getAllPricingTiers()
  }
}
